<!-- Laid out as the chat window is: a header bar that drags the pane, the panels as cards, the
     status lines and hints, the actions at the foot. The frame around it is src/_cura-window.scss
     (panel class ai-recording-panel). This dialog is the recording workflow, so every binding,
     guard and state below is as it was; only the frame and classes changed. -->

<!-- full-size header -->
<div class="cw-header" *ngIf="!isMinimized; else minimizedTpl" appDraggableOverlayPane>
    <mat-icon class="kind">{{ type === 'ward-round' ? 'groups' : 'mic' }}</mat-icon>
    <div class="title">
        <span class="name">{{ type === 'ward-round' ? 'AI Ward Round' : 'AI Note Generator' }}</span>
        <span class="subtitle">{{ patientName || (type === 'ward-round' ? 'Record the round and MedicAI writes the notes' : 'Record the consultation and MedicAI writes the note') }}</span>
    </div>
    <button mat-icon-button *ngIf="isRecording && type !== 'ward-round'" (click)="minimize()" matTooltip="Minimize"
        aria-label="Minimize">
        <mat-icon>fullscreen_exit</mat-icon>
    </button>
</div>

<!-- minimized bar -->
<ng-template #minimizedTpl>
    <div class="minimized-bar" [class.stalled]="recordingStalled" [class.paused]="isPaused" appDraggableOverlayPane
        [matTooltip]="patient | patientHeading: 'Recording'">
        <!-- The minimized bar is where a consultation is normally recorded from, so the
             stall has to be legible here and not only on the restored panel. -->
        <mat-icon *ngIf="recordingStalled" class="stalled-icon"
            [matTooltip]="recordingStalledMessage">mic_off</mat-icon>
        <span class="rec-dot" *ngIf="!recordingStalled"></span>
        <span class="time">{{ recordingTime | date:'mm:ss' }}</span>
        <button mat-icon-button (click)="toggleRecording()" [disabled]="isLoading || recordingStalled">
            <mat-icon *ngIf="!isRecording || isPaused">mic</mat-icon>
            <mat-icon *ngIf="isRecording && !isPaused">pause</mat-icon>
        </button>
        <button mat-mini-fab color="warn" (click)="stopRecording()" [disabled]="!isRecording || isLoading">
            <mat-icon>stop</mat-icon>
        </button>
        <button mat-icon-button (click)="restore()" matTooltip="Restore">
            <mat-icon>fullscreen</mat-icon>
        </button>
    </div>
</ng-template>

<div mat-dialog-content class="ai-dialog-container cw-body" *ngIf="!isMinimized">

    <div class="network-overlay" *ngIf="showNetworkWarning">
        <h3>Slow / No Connection Detected</h3>

        <div *ngIf="!speedResult && hasActiveNetworkConnection">
            <button mat-stroked-button class="quiet-button" (click)="runSpeedTest()" [disabled]="isRunningSpeedTest">
                {{ isRunningSpeedTest ? 'Running test…' : 'Run speed-test' }}
            </button>
        </div>

        <div *ngIf="!hasActiveNetworkConnection">
            <p class="warning">You are not connected to the internet. Please connect to a network to continue.</p>
        </div>

        <div class="speed-results" *ngIf="speedResult">
            <p>
                <mat-icon class="direction">arrow_downward</mat-icon>
                <span>Download</span>
                <strong>{{ speedResult.down }} Mb/s</strong>
            </p>

            <p>
                <mat-icon class="direction">arrow_upward</mat-icon>
                <span>Upload</span>
                <strong>{{ speedResult.up }} Mb/s</strong>
            </p>

            <div class="speed-verdict" *ngIf="speedVerdict" [ngClass]="speedVerdict.level">
                <mat-icon>{{ speedVerdict.icon }}</mat-icon>
                <span>{{ speedVerdict.msg }}</span>
            </div>

            <button mat-stroked-button class="quiet-button" (click)="runSpeedTest()"
                [disabled]="isRunningSpeedTest || cooldownRemaining > 0">
                <ng-container *ngIf="isRunningSpeedTest">Running test…</ng-container>
                <ng-container *ngIf="!isRunningSpeedTest && cooldownRemaining === 0">Test Again</ng-container>
                <ng-container *ngIf="!isRunningSpeedTest && cooldownRemaining > 0">
                    Test Again ({{ cooldownRemaining }}s)
                </ng-container>
            </button>
        </div>

        <p class="m-t-10" *ngIf="downloadEnabled">
            Would you like to download the recorded audio to your device?
        </p>

        <div class="actions">
            <ng-container *ngIf="downloadEnabled">
                <button mat-button class="quiet-button" (click)="handleNetworkDecision(false)">No</button>
                <button mat-raised-button class="tone-button" (click)="handleNetworkDecision(true)">Yes -
                    download</button>
            </ng-container>
            <button mat-stroked-button class="quiet-button" *ngIf="retryEnabled" (click)="retryProcessing()">Retry
                Processing</button>
            <ng-container *ngIf="!downloadEnabled">
                <button mat-raised-button class="tone-button" (click)="handleNetworkDecision(false)">Close</button>
            </ng-container>

        </div>
    </div>

    <div class="ai-locked" *ngIf="lockProcessing && !isPremium">
        <mat-icon>error</mat-icon>
        <p class="p-l-10">You do not have enough AI tokens to continue utilizing AI features. Please recharge your
            token
            balance by navigating to settings and selecting the "Subscriptions & Usage" tab.</p>
    </div>

    <ng-container *ngIf="!lockProcessing">
        <div class="token-balance-warning" *ngIf="!isPremium && tokenBalance && tokenBalance <= 100">
            <mat-icon>warning</mat-icon>
            <p class="p-l-10">Your token balance is currently R{{ tokenBalance }}. Please
                recharge soon to ensure you are able to continue utilizing AI features.</p>
        </div>

        <div [hidden]="aiError || uploadError || isLoading || aiCompleted">

            <mat-accordion class="cw-cards" [multi]="false">
                <!-- Locked open while recording: `disabled` is what blocks the toggle for both
                     mouse and keyboard, and `hideToggle` removes the chevron that would
                     otherwise promise it can still be collapsed. The greyed-out text Material
                     gives a disabled header is overridden in the SCSS — this panel is locked,
                     not unavailable. -->
                <mat-expansion-panel class="live-recording-panel"
                    [class.locked-open]="isRecording" [expanded]="liveRecordingExpanded"
                    (expandedChange)="liveRecordingExpanded = $event" [disabled]="isRecording"
                    [hideToggle]="isRecording">
                    <mat-expansion-panel-header>
                        <mat-panel-title><mat-icon class="m-r-10">mic_none</mat-icon>Live
                            Recording</mat-panel-title>
                    </mat-expansion-panel-header>

                    <ng-container *ngTemplateOutlet="recordingContainer"></ng-container>
                </mat-expansion-panel>

                <mat-expansion-panel [expanded]="false" [disabled]="isRecording"
                    *ngIf="type !== 'ward-round'">
                    <mat-expansion-panel-header
                        [matTooltip]="isRecording ? 'Not available while a recording is in progress' : ''">
                        <mat-panel-title><mat-icon class="m-r-10">cloud_upload</mat-icon> Audio
                            Upload</mat-panel-title>
                    </mat-expansion-panel-header>
                    <div class="audio-upload-container">
                        <div class="audio-upload">
                            <div class="dropzone cw-dropzone" [class.dragover]="isDragOver" (dragover)="onDragOver($event)"
                                (dragleave)="onDragLeave($event)" (drop)="onDrop($event)">
                                <input type="file" accept="audio/*,.wav,audio/wav,audio/x-wav,audio/mp4,video/mp"
                                    (change)="onFileSelected($event)" class="file-input" id="fileInput" />
                                <label for="fileInput" class="dropzone-label">
                                    <div class="dropzone-content">
                                        <mat-icon>cloud_upload</mat-icon>
                                        <p *ngIf="!selectedFile && !audioUrl">Drag and drop an audio file here or
                                            click
                                            to
                                            select a file
                                        </p>
                                        <p *ngIf="selectedFile">{{ selectedFile.name }}</p>
                                        <p *ngIf="audioUrl">Cached recording loaded</p>
                                    </div>
                                </label>
                            </div>
                            <div *ngIf="fileTypeError" class="error-message">{{ fileTypeError }}</div>
                        </div>
                    </div>
                </mat-expansion-panel>

                <mat-expansion-panel [expanded]="false" *ngIf="type === 'ward-round'">
                    <mat-expansion-panel-header>
                        <mat-panel-title><mat-icon class="m-r-10">document_scanner</mat-icon> Note
                            Scanner</mat-panel-title>
                    </mat-expansion-panel-header>
                    <app-file-upload [fileObjects]="[]" [allowMultiple]="false" [allowedFileTypes]="'image/*'"
                        title="Note Image" (fileUpload)="onNoteScanUpload($event)"
                        (fileDelete)="onNoteScanDelete($event)" *ngIf="!noteScanProcessing" [cameraEnabled]="true">
                    </app-file-upload>

                    <ng-container *ngIf="noteScanProcessing">
                        <div class="cw-processing">
                            <mat-spinner diameter="24"></mat-spinner>
                            <span>Processing...</span>
                        </div>
                        <div class="cw-status"
                            [ngClass]="!noteScanProcessing ? 'completed' : 'pending'">
                            <span>Extracting note information</span>
                        </div>

                        <p class="cw-hint"><strong>Note: </strong>MedicAI can make mistakes. We advise that you check
                            the
                            content of the output carefully before accepting it into the patient file.</p>
                    </ng-container>
                </mat-expansion-panel>
            </mat-accordion>
        </div>

        <div class="dialog-footer">

            <p class="cw-hint">
                By proceeding, you agree to the <a href="assets/documents/IQject Terms of service - v0.5.pdf"
                    target="_blank">terms of service</a> and <a
                    href="assets/documents/IQject privacy notice - 21 January 2025.pdf" target="_blank">privacy
                    policy</a>.
            </p>

            <p class="cw-hint" *ngIf="isRecording">Note: Once you press stop, your recording will begin processing to
                generate
                your notes</p>

            <ng-container *ngIf="(!aiError && !uploadError) && (isLoading || uploadStatus || aiStatus)">


                <p class="cw-hint" *ngIf="aiStatus">Note: Depending on the length of your recording, this could take up
                    to
                    3
                    minutes to complete. It is now safe to close this window. You will be notified when
                    processing has completed.</p>

                <div class="cw-processing" *ngIf="isLoading">
                    <mat-spinner diameter="24"></mat-spinner>
                    <span>Processing...</span>
                </div>
                <div class="cw-status" *ngIf="uploadStatus"
                    [ngClass]="uploadCompleted ? 'completed' : 'pending'">
                    <mat-icon *ngIf="uploadCompleted">check_circle</mat-icon><span
                        *ngIf="uploadStatus">{{
                        uploadStatus
                        }}</span>
                </div>

                <div class="cw-status" *ngIf="aiStatus"
                    [ngClass]="aiCompleted ? 'completed' : 'pending'">
                    <mat-icon *ngIf="aiCompleted">check_circle</mat-icon><span *ngIf="aiStatus">{{
                        aiStatus
                        }}</span>
                </div>

                <p class="cw-hint" *ngIf="downloadEnabled && !disableAutoDownload">
                    Important: Your audio recording has been automatically downloaded to your device as a backup.
                    You
                    can reupload this file for processing if the output is not as expected.
                </p>


                <p class="cw-hint">Note: MedicAI can make mistakes. We advise that you check the
                    content of the output carefully before accepting it into the patient file.</p>

            </ng-container>


            <div class="error-container" *ngIf="aiError || uploadError">
                <div class="cw-status error">
                    <mat-icon>error_outline</mat-icon><span>An error has occurred. Please
                        try again. Your audio file is available for download using the link below.</span>
                </div>
                <button mat-raised-button class="tone-button" (click)="downloadCurrentAudioFile()">Download Audio
                    File</button>
                <button mat-raised-button class="tone-button" *ngIf="retryEnabled" (click)="retryProcessing()">Retry
                    Processing</button>
            </div>
        </div>
    </ng-container>
</div>
<div mat-dialog-actions class="cw-actions" *ngIf="!isMinimized">
    <button mat-stroked-button class="quiet-button" *ngIf="isRecording && type !== 'ward-round'" (click)="minimize()"
        matTooltip="Minimize">
        <mat-icon>fullscreen_exit</mat-icon> Minimize Dialog
    </button>
    <button mat-stroked-button class="quiet-button" (click)="downloadCurrentAudioFile()"
        *ngIf="downloadEnabled && (finalResult || reachedTimeout)">Download
        Audio File</button>
    <span class="spacer"></span>
    <button mat-raised-button [color]="isRecording ? 'warn' : 'primary'" [class.tone-button]="!isRecording"
        (click)="closeDialog()" cdkFocusInitial
        *ngIf="shouldShowCloseButton()" [ngClass]="finalResult ? 'ai-feature' : ''">{{isRecording
        ?
        'Delete' :
        (finalResult ? 'View' :
        'Close')}}</button>

</div>

<ng-template #recordingContainer>
    <div class="recording-container">

        <div class="token-balance-warning" *ngIf="hasAiRequestsForToday && !isRecording && !isLoading">
            <mat-icon>warning</mat-icon>
            <p class="p-l-10">
                An AI request for this patient was created today{{ lastAiRequestTime ? (' at ' +
                lastAiRequestTime) : '' }}.
                Please confirm you're recording against the correct patient.
            </p>
        </div>

        <div class="recording-stalled-warning" *ngIf="recordingStalled">
            <mat-icon>mic_off</mat-icon>
            <p class="p-l-10">{{ recordingStalledMessage }}</p>
        </div>

        <div class="recording-status">
            <div class="status-indicator"
                [ngClass]="{'recording': isRecording && !recordingStalled, 'paused': isPaused, 'stalled': recordingStalled}">
            </div>
            <span *ngIf="recordingStalled">Recording stopped</span>
            <span *ngIf="isRecording && !recordingStalled">Recording...</span>
            <span *ngIf="isPaused && !recordingStalled">{{ recordingInterrupted ? 'Paused: microphone interrupted' : 'Paused' }}</span>
            <span *ngIf="!isRecording && !isPaused && !recordingStalled">Not Recording</span>
            <span *ngIf="isRecording" class="recording-time">{{ recordingTime | date:'mm:ss' }}</span>
        </div>

        <div class="recording-controls">
            <button mat-fab class="ai-feature" (click)="toggleRecording()"
                [disabled]="isLoading || recordingStalled">
                <mat-icon *ngIf="!isRecording || isPaused">mic</mat-icon>
                <mat-icon *ngIf="isRecording && !isPaused">pause</mat-icon>
            </button>
            <button mat-mini-fab color="warn" (click)="stopRecording()" [disabled]="!isRecording || isLoading"
                class="m-l-10" *ngIf="!isPaused">
                <mat-icon>stop</mat-icon>
            </button>
        </div>

        <div class="playback-controls" *ngIf="isPlaybackEnabled">
            <audio [src]="audioUrl" controls class="audio-player"></audio>
        </div>
    </div>
</ng-template>
